import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import { trackAppMetric } from "./appMetrics";
import { stripUndefinedDeep } from "./stripUndefinedDeep";

export const CONTENT_BOX_STALE_UPDATE_MESSAGE =
  "This content box no longer exists. Please refresh before trying again.";

export const CONTENT_BOX_STALE_ERROR_CODE = "content-box-stale";

export class StaleContentBoxError extends Error {
  constructor(message = CONTENT_BOX_STALE_UPDATE_MESSAGE) {
    super(message);
    this.name = "StaleContentBoxError";
    this.code = CONTENT_BOX_STALE_ERROR_CODE;
  }
}

export function isStaleContentBoxError(error) {
  if (!error) return false;
  if (error instanceof StaleContentBoxError) return true;
  if (error?.code === CONTENT_BOX_STALE_ERROR_CODE) return true;
  const message = String(error?.message || "");
  return message === CONTENT_BOX_STALE_UPDATE_MESSAGE;
}

export function isMissingContentBoxUpdateError(error) {
  const message = String(error?.message || "");
  return error?.code === "not-found" || message.includes("No document to update");
}

export function buildContentBoxRef(userId, classId, contentBoxId) {
  return doc(
    db,
    FIREBASE_COLLECTIONS.USERS,
    userId,
    FIREBASE_COLLECTIONS.CLASSES,
    classId,
    FIREBASE_COLLECTIONS.CONTENT_BOXES,
    contentBoxId
  );
}

/**
 * updateDoc against a content box that may already be deleted/replaced.
 * Returns { ok, stale } instead of throwing Firebase not-found errors.
 */
export async function updateExistingContentBoxDoc({
  userId,
  classId,
  contentBoxId,
  payload,
  metricName = "content_box.update",
} = {}) {
  if (!userId || !classId || !contentBoxId || !payload || typeof payload !== "object") {
    trackAppMetric(metricName, { outcome: "invalid_args" });
    return { ok: false, stale: false };
  }

  const sanitizedPayload = stripUndefinedDeep(payload);
  if (
    !sanitizedPayload
    || typeof sanitizedPayload !== "object"
    || Array.isArray(sanitizedPayload)
    || Object.keys(sanitizedPayload).length === 0
  ) {
    trackAppMetric(metricName, { outcome: "invalid_args" });
    return { ok: false, stale: false };
  }

  const contentBoxRef = buildContentBoxRef(userId, classId, contentBoxId);
  const contentBoxSnap = await getDoc(contentBoxRef);

  if (!contentBoxSnap.exists()) {
    trackAppMetric(metricName, {
      outcome: "stale",
      reason: "missing_doc",
    });
    return { ok: false, stale: true };
  }

  try {
    await updateDoc(contentBoxRef, sanitizedPayload);
  } catch (error) {
    if (isMissingContentBoxUpdateError(error)) {
      trackAppMetric(metricName, {
        outcome: "stale",
        reason: "not_found_update",
      });
      return { ok: false, stale: true };
    }
    throw error;
  }

  trackAppMetric(metricName, { outcome: "success" });
  return { ok: true, stale: false };
}

/**
 * Prefer existing docs for batch.update paths. Missing IDs are returned separately
 * so callers can mark them failed without failing the whole batch.
 */
export async function partitionExistingContentBoxIds(userId, classId, contentBoxIds = []) {
  const normalizedIds = [...new Set(contentBoxIds.map((id) => String(id || "")).filter(Boolean))];
  const existingIds = [];
  const missingIds = [];

  if (!userId || !classId || !normalizedIds.length) {
    return { existingIds, missingIds: normalizedIds };
  }

  const existenceResults = await Promise.all(
    normalizedIds.map(async (contentBoxId) => {
      const contentBoxRef = buildContentBoxRef(userId, classId, contentBoxId);
      try {
        const snap = await getDoc(contentBoxRef);
        return { contentBoxId, exists: snap.exists() };
      } catch (_) {
        // Treat load failures as missing for batch safety; per-box fallback can retry.
        return { contentBoxId, exists: false };
      }
    })
  );

  existenceResults.forEach(({ contentBoxId, exists }) => {
    if (exists) {
      existingIds.push(contentBoxId);
    } else {
      missingIds.push(contentBoxId);
    }
  });

  return { existingIds, missingIds };
}
